import type { RouteRecordRaw } from 'vue-router'
import { CAPABILITIES as ADMIN_CAPABILITIES } from '~/modules/admin/nav'
import { CAPABILITIES as AFFILIATE_CAPABILITIES } from '~/modules/affiliate/constants'
import { CAPABILITIES } from '~/modules/landlord/constants'

/**
 * Tenancies, the renewal board, payments and receipts (Phase 07) — for both workspaces.
 *
 * The same pages are registered under `/app/landlord/*` and `/app/affiliate/*`; each reads
 * `meta.workspace` to pick its API prefix, its links and what it may offer. The officer's copy
 * is read-only — no renewal recording, no move-out (affiliate-workspace.md §3.2) — with one
 * exception: adding a tenant to a vacant space where the landlord opted the property in or the
 * officer onboarded it (backend phase-07 §3.2; the server decides, the property page shows the
 * door). Declared here rather than in the two workspace modules so the twins cannot drift —
 * the pattern the onboarding wizard set.
 */
const landlord = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'landlord' as const,
}
const affiliate = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'affiliate' as const,
}
const admin = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'admin' as const,
  capability: ADMIN_CAPABILITIES.adminAccess,
}

export const tenancyRoutes: RouteRecordRaw[] = [
  // ---- Landlord & Agent ---------------------------------------------------------------
  {
    path: '/app/landlord/tenants',
    name: 'landlord-tenants',
    component: () => import('./pages/TenancyListPage.vue'),
    meta: { ...landlord, capability: CAPABILITIES.tenantView, title: 'landlord.nav.tenants' },
  },
  {
    path: '/app/landlord/tenants/:id(\\d+)',
    name: 'landlord-tenant-detail',
    component: () => import('./pages/TenancyDetailPage.vue'),
    meta: { ...landlord, capability: CAPABILITIES.tenantView, title: 'landlord.nav.tenants' },
  },
  {
    path: '/app/landlord/spaces/:id(\\d+)/tenants/new',
    name: 'landlord-tenant-new',
    component: () => import('./pages/TenantCreatePage.vue'),
    meta: { ...landlord, capability: CAPABILITIES.tenantManage, title: 'landlord.nav.tenants' },
  },
  {
    path: '/app/landlord/renewals',
    name: 'landlord-renewals',
    component: () => import('./pages/RenewalBoardPage.vue'),
    meta: { ...landlord, capability: CAPABILITIES.rentView, title: 'landlord.nav.renewals' },
  },
  {
    path: '/app/landlord/payments',
    name: 'landlord-payments',
    component: () => import('./pages/PaymentsPage.vue'),
    meta: { ...landlord, capability: CAPABILITIES.rentView, title: 'landlord.nav.payments' },
  },
  {
    path: '/app/landlord/payments/:id',
    name: 'landlord-payment-detail',
    component: () => import('./pages/PaymentDetailPage.vue'),
    meta: { ...landlord, capability: CAPABILITIES.rentView, title: 'landlord.nav.payments' },
  },
  // ---- Administration (read-only) --------------------------------------------------------
  // The tenant list over every tenancy on the platform (2026-09-29, the user's call): the
  // landlord's page, filters and table, from `/api/v2/admin/tenancies/`.
  {
    path: '/app/admin/tenants',
    name: 'admin-tenants',
    component: () => import('./pages/TenancyListPage.vue'),
    meta: { ...admin, title: 'admin.nav.tenants' },
  },
  // ---- Affiliate Operations (read-only) ------------------------------------------------
  {
    path: '/app/affiliate/tenants',
    name: 'affiliate-tenants',
    component: () => import('./pages/TenancyListPage.vue'),
    meta: {
      ...affiliate,
      capability: AFFILIATE_CAPABILITIES.tenantView,
      title: 'affiliate.nav.tenants',
    },
  },
  {
    path: '/app/affiliate/tenants/:id(\\d+)',
    name: 'affiliate-tenant-detail',
    component: () => import('./pages/TenancyDetailPage.vue'),
    meta: {
      ...affiliate,
      capability: AFFILIATE_CAPABILITIES.tenantView,
      title: 'affiliate.nav.tenants',
    },
  },
  {
    path: '/app/affiliate/spaces/:id(\\d+)/tenants/new',
    name: 'affiliate-tenant-new',
    component: () => import('./pages/TenantCreatePage.vue'),
    meta: {
      ...affiliate,
      capability: AFFILIATE_CAPABILITIES.tenantView,
      title: 'affiliate.nav.tenants',
    },
  },
  {
    path: '/app/affiliate/renewals',
    name: 'affiliate-renewals',
    component: () => import('./pages/RenewalBoardPage.vue'),
    meta: {
      ...affiliate,
      capability: AFFILIATE_CAPABILITIES.rentView,
      title: 'affiliate.nav.renewals',
    },
  },
  {
    path: '/app/affiliate/payments',
    name: 'affiliate-payments',
    component: () => import('./pages/PaymentsPage.vue'),
    meta: {
      ...affiliate,
      capability: AFFILIATE_CAPABILITIES.rentView,
      title: 'affiliate.nav.payments',
    },
  },
  {
    path: '/app/affiliate/payments/:id',
    name: 'affiliate-payment-detail',
    component: () => import('./pages/PaymentDetailPage.vue'),
    meta: {
      ...affiliate,
      capability: AFFILIATE_CAPABILITIES.rentView,
      title: 'affiliate.nav.payments',
    },
  },
]
